import { ButtonComponent, IconButton } from '@/components/commons'
import { RadioGroup } from '@/components/radioGroup/RadioGroup'
import { ModalPage } from '@/components/ui/modals/ModalPage.component'
import { blockConnection } from '@/services/connections'
import { useMutation } from '@/shared/hooks/useMutation'
import { Slot } from '@radix-ui/react-slot'
import Link from 'next/link'
import React, { createContext, useCallback, useContext, useState } from 'react'
import { UserContext } from '../../context'
import {
  modalCloseButton,
  modalContainer,
  modalContentContainer,
  modalFooterContainer,
  modalHeaderContainer,
} from '../requestConnectionButton/RequestConnectionButton.css'
import { blockConnectionLink } from './BlockConnectionModal.css'

type BlockConnectionModalContextType = { showBlockConnectionModal: () => void }
const BlockConnectionModalContext = createContext(
  {} as BlockConnectionModalContextType
)

export const BlockConnectionModal = ({
  blockedUserId,
  children,
  onSuccess,
}: {
  blockedUserId: string
  children: React.ReactNode
  onSuccess?: () => void
}) => {
  const { setUser } = React.useContext(UserContext)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [blockReason, setBlockReason] = useState('')

  const onCloseModal = useCallback(() => {
    setIsModalVisible(false)
    setBlockReason('')
  }, [])

  const [mutate, { isLoading }] = useMutation({
    mutateMethod: blockConnection,
  })

  const onSubmit = useCallback(async () => {
    const res = await mutate(blockedUserId, blockReason)
    if ('error' in res) return
    if (res?.id) {
      setUser(res)
    }
    if (onSuccess) onSuccess()
    onCloseModal()
  }, [blockedUserId, onSuccess, blockReason, mutate, setUser, onCloseModal])

  return (
    <BlockConnectionModalContext.Provider
      value={{ showBlockConnectionModal: () => setIsModalVisible(true) }}
    >
      <ModalPage
        classNameContainer={[modalContentContainer]}
        show={isModalVisible}
        hideCloseIcon
        onClose={onCloseModal}
        className={[modalContainer]}
      >
        <div className={modalHeaderContainer}>
          <h3 className="text-left m-0 text-center--tablet text-primary font-bold  text-18 text-20--tablet">
            Block Connection
          </h3>
          <IconButton
            className={modalCloseButton}
            icon="CloseIcon"
            onClick={onCloseModal}
          />
        </div>
        <div className="flex-1">
          <p className="text-tertirary">
            Please select the reason why you want to block this user
          </p>
          <RadioGroup onChange={setBlockReason}>
            <RadioGroup.Item value="do-not-know-user">
              I do not know this user
            </RadioGroup.Item>
            <RadioGroup.Item value="harassing-user">
              This user is harassing me
            </RadioGroup.Item>
            <RadioGroup.Item value="impersonating-user">
              This user is impersonating me or someone else
            </RadioGroup.Item>
            <RadioGroup.Item value="violating-terms">
              I believe this user is violating the{' '}
              <Link
                target="_blank"
                href="/terms"
                className={blockConnectionLink}
              >
                terms of service
              </Link>
            </RadioGroup.Item>
          </RadioGroup>
        </div>
        <div className={modalFooterContainer}>
          <ButtonComponent
            disabled={!blockReason}
            onClick={onSubmit}
            loading={isLoading}
            label="Block"
          />
        </div>
      </ModalPage>
      {children}
    </BlockConnectionModalContext.Provider>
  )
}

export const BlockConnectionModalTrigger = ({ children }) => {
  const { showBlockConnectionModal } = useContext(BlockConnectionModalContext)

  return <Slot onClick={showBlockConnectionModal}>{children}</Slot>
}
